import { SearchOutlined } from '@ant-design/icons';
import { Select } from 'antd5';
import type { BaseSelectRef } from 'rc-select';
import {
  type MouseEvent as ReactMouseEvent,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';

import { filterOption } from 'components/choosers/helpers';
import { getAssetColumnCopy } from 'components/data/assets/assetColumnRenderers';
import { CustomFilterContainer, CustomFilterPopover } from 'components/data/helpers/filters.style';
import { getTableSiteFilterIds } from 'components/data/helpers/tableFilterUtils';
import { DataTableContext } from 'components/DataTable';
import {
  FilterMultiSelectContainer,
  handleTableFilterActionMouseDown,
  TableFilterMultiSelect,
} from 'components/DataTable/customFilters/TableFilterMultiSelect';
import { Button, Tooltip } from 'components/ui';
import { AutoFocus } from 'horizon/components/AutoFocus';
import type { CustomFilterDropdownProps } from 'horizon/types/TableColumnDef';
import type { AtlasGqlAsset } from 'types/atlas-graphql';
import { useGetAssetsForAssetFilterQuery, useGetAssetTypesMinimalQuery } from 'types/atlas-graphql';
import { naturalSortBy } from 'utils/naturalSort';

const PAGE_SIZE = 500;

/**
 * Table column filter component for filtering assets in an asset table.
 *
 * This component takes an asset type name a props, and provides a list of existing assets of that
 * type in the current organization.
 * For non-site tables, if a site filter is applied, the values are limited to those at those at
 * the selected site(s).
 */
type SimpleAssetFilterProps = CustomFilterDropdownProps & {
  assetTypeName: string;
  defaultSelectedSiteIds?: string[];
  requireSiteFilter?: boolean;
  requireSiteFilterTooltip?: string;
};

export const SimpleAssetFilter: React.FunctionComponent<SimpleAssetFilterProps> = ({
  assetTypeName,
  defaultSelectedSiteIds,
  requireSiteFilter = false,
  requireSiteFilterTooltip,
  setSelectedKeys,
  selectedKeys,
  confirm,
  clearFilters,
}) => {
  const { t } = useTranslation();
  const inputRef = useRef<BaseSelectRef>(null);
  const [allAssets, setAllAssets] = useState<AtlasGqlAsset[]>([]);
  // Keep search text across selections so users can bulk-select from a filtered list.
  const [searchValue, setSearchValue] = useState('');

  const { filteredInfo } = useContext(DataTableContext);
  const siteFilterKey = JSON.stringify(filteredInfo?.site ?? []);
  const defaultSelectedSiteIdsKey = JSON.stringify(defaultSelectedSiteIds ?? []);
  const selectedSiteIds: string[] = useMemo(() => {
    const siteIds = getTableSiteFilterIds({ site: JSON.parse(siteFilterKey) });
    if (siteIds.length) {
      return siteIds;
    }

    return JSON.parse(defaultSelectedSiteIdsKey) as string[];
  }, [defaultSelectedSiteIdsKey, siteFilterKey]);

  const { data: typesData, loading: typesLoading } = useGetAssetTypesMinimalQuery();
  const assetTypeId: string | undefined = typesData?.assetTypes.find(
    ({ name }) => name === assetTypeName
  )?.id;

  const { data, loading, fetchMore } = useGetAssetsForAssetFilterQuery({
    variables: {
      filterBy: [
        ...(assetTypeId ? [{ key: 'assetTypeIds', values: [assetTypeId] }] : []),
        ...(selectedSiteIds.length ? [{ key: 'descendantOfId', values: selectedSiteIds }] : []),
      ],
      limit: PAGE_SIZE,
    },
    skip: !assetTypeId,
    notifyOnNetworkStatusChange: true,
  });

  const siteFilteringRequired = useMemo(() => {
    if (requireSiteFilter) {
      return !selectedSiteIds.length;
    }

    return !selectedSiteIds.length && (data?.assetsWithMetadata.totalCount ?? 0) > 1500;
  }, [data?.assetsWithMetadata?.totalCount, requireSiteFilter, selectedSiteIds]);

  const siteFilteringTooltip = requireSiteFilter
    ? requireSiteFilterTooltip ??
      t('table.setSiteFilterBeforeTurbine', 'Set Site filter before setting Turbine filter.')
    : t(
        'table.selectSiteInColumnFilter',
        'Please select at least one site in the site column filter'
      );

  /**
   * Set allAssets state when the initial query responds, then progressively fetch
   * subsequent pages and merge them into state.
   */
  useEffect(() => {
    if (!data?.assetsWithMetadata?.items) {
      return;
    }

    const initialItems = data.assetsWithMetadata.items.filter((a): a is AtlasGqlAsset => !!a);
    const totalCount = data.assetsWithMetadata.totalCount;
    setAllAssets(initialItems);

    if (siteFilteringRequired || initialItems.length >= totalCount) {
      return;
    }

    let cancelled = false;
    (async () => {
      let accumulated = initialItems;
      let nextOffset = data.assetsWithMetadata.items.length;
      while (accumulated.length < totalCount) {
        const result = await fetchMore({
          variables: { offset: nextOffset },
        });
        if (cancelled) {
          return;
        }
        const rawPage = result.data?.assetsWithMetadata?.items ?? [];
        if (!rawPage.length) {
          break;
        }
        const newItems = rawPage.filter((a): a is AtlasGqlAsset => !!a);
        nextOffset += rawPage.length;
        accumulated = [...accumulated, ...newItems];
        setAllAssets(accumulated);
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, siteFilteringRequired]);

  // Clean up available options when the values they depend on change
  useEffect(() => {
    if (!selectedSiteIds?.length || !assetTypeId) {
      setAllAssets([]);
    }
  }, [selectedSiteIds, assetTypeId]);

  const assetOptions = useMemo(
    () =>
      allAssets
        .map((asset: AtlasGqlAsset) => ({
          id: asset.id,
          displayName: getAssetColumnCopy({
            asset,
            includeSite: selectedSiteIds?.length !== 1,
            separatorText: ' | ',
          }),
        }))
        .sort(naturalSortBy('displayName')),
    [allAssets, selectedSiteIds]
  );

  const handleConfirm = () => {
    setSearchValue('');
    confirm({ closeDropdown: true });
  };

  const handleReset = () => {
    setSearchValue('');
    clearFilters && clearFilters();
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      setSearchValue('');
    }
  };

  const assetSelect = (
    <TableFilterMultiSelect
      id="asset-filter-dropdown"
      ref={inputRef}
      placeholder={t('common.selectAssetPlaceholder', 'Select asset...')}
      disabled={siteFilteringRequired}
      loading={loading || typesLoading}
      allowClear
      showSearch
      filterOption={filterOption}
      virtual={false}
      value={(selectedKeys as string[]) ?? []}
      onChange={setSelectedKeys}
      searchValue={searchValue}
      onSearch={setSearchValue}
      onOpenChange={handleOpenChange}
      popupMatchSelectWidth={false}
      suffixIcon={<SearchOutlined aria-hidden />}
    >
      {assetOptions.map(({ id, displayName }) => (
        <Select.Option key={id} className="asset-filter-option" value={id}>
          {displayName}
        </Select.Option>
      ))}
    </TableFilterMultiSelect>
  );

  return (
    <AutoFocus elementToFocus={inputRef}>
      <CustomFilterPopover className="ant-table-filter-dropdown">
        <CustomFilterContainer>
          <FilterMultiSelectContainer>
            {siteFilteringRequired ? (
              <Tooltip title={siteFilteringTooltip}>{assetSelect}</Tooltip>
            ) : (
              assetSelect
            )}
          </FilterMultiSelectContainer>
        </CustomFilterContainer>
        <div className="ant-table-filter-dropdown-btns" style={{ borderTop: '1px solid #f0f0f0' }}>
          <Button
            type="link"
            size="small"
            onMouseDown={(event: ReactMouseEvent) =>
              handleTableFilterActionMouseDown(event, handleReset)
            }
          >
            {t('table.reset', 'Reset')}
          </Button>
          <Button
            type="primary"
            size="small"
            onMouseDown={(event: ReactMouseEvent) =>
              handleTableFilterActionMouseDown(event, handleConfirm)
            }
          >
            {t('table.ok', 'OK')}
          </Button>
        </div>
      </CustomFilterPopover>
    </AutoFocus>
  );
};
